import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTour } from '../../context/TourContext'
import { TourStep } from '../../data/tourSteps'
import styles from './TourOverlay.module.css'

interface TargetRect {
  top: number
  left: number
  width: number
  height: number
}

const PADDING = 8

function findTargetRect(step: TourStep): TargetRect | null {
  if (step.target === 'content:general') return null

  if (step.target.startsWith('sidebar:')) {
    const tabId = step.target.replace('sidebar:', '')
    const candidates = document.querySelectorAll(`[data-tour-tab="${tabId}"]`)
    for (const el of Array.from(candidates)) {
      const rect = el.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        return { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
      }
    }
    return null
  }

  if (step.target.startsWith('admin:')) {
    const section = step.target.replace('admin:', '')
    const candidates = document.querySelectorAll(`[data-tour-admin="${section}"]`)
    for (const el of Array.from(candidates)) {
      const rect = el.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        return { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
      }
    }
    return null
  }

  if (step.target.startsWith('report-tab:')) {
    const reportId = step.target.replace('report-tab:', '')
    const btn = document.querySelector(`[data-tour-report="${reportId}"]`) as HTMLElement | null
    if (btn) {
      const rect = btn.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        return { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
      }
    }
    return null
  }

  return null
}

function computeTooltipPosition(
  targetRect: TargetRect | null,
  tooltipRef: React.RefObject<HTMLDivElement>,
  isSidebarTarget: boolean,
  isReportTabTarget: boolean = false,
): { top: number; left: number; arrowTop: number; arrowLeft: number; arrowRotation: number } {
  const tooltip = tooltipRef.current
  const tooltipWidth = tooltip?.offsetWidth || 380
  const tooltipHeight = tooltip?.offsetHeight || 260
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const margin = 16
  const gap = 14

  if (!targetRect) {
    return {
      top: Math.max(margin, (viewportHeight - tooltipHeight) / 2),
      left: Math.max(margin, (viewportWidth - tooltipWidth) / 2),
      arrowTop: -7,
      arrowLeft: -7,
      arrowRotation: 0,
    }
  }

  const targetCenterX = targetRect.left + targetRect.width / 2
  const targetBottom = targetRect.top + targetRect.height
  const targetTop = targetRect.top
  const targetCenterY = targetRect.top + targetRect.height / 2

  if (isReportTabTarget) {
    // Position centered horizontally, below the report tabs bar.
    let left = (viewportWidth - tooltipWidth) / 2
    left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin))
    let top = targetBottom + gap
    top = Math.max(margin, Math.min(top, viewportHeight - tooltipHeight - margin))

    // Arrow points up at the tab, horizontally centered on the tab
    const arrowLeft = Math.max(20, Math.min(targetCenterX - left - 7, tooltipWidth - 20))
    return { top, left, arrowTop: -7, arrowLeft, arrowRotation: 45 }
  }

  if (isSidebarTarget) {
    // Place tooltip to the right of the sidebar, vertically aligned with the target item.
    const rightEdge = targetRect.left + targetRect.width
    let left = rightEdge + gap
    let arrowRotation = 315 // arrow pointing left toward the sidebar item
    let arrowLeft = -7
    let arrowTop: number

    let top = targetCenterY - tooltipHeight / 2
    top = Math.max(margin, Math.min(top, viewportHeight - tooltipHeight - margin))
    arrowTop = Math.max(20, Math.min(targetCenterY - top - 7, tooltipHeight - 20))

    // If the tooltip would overflow the right edge, place it to the left of the sidebar instead.
    if (left + tooltipWidth > viewportWidth - margin) {
      left = Math.max(margin, targetRect.left - tooltipWidth - gap)
      arrowRotation = 135 // arrow pointing right
      arrowLeft = tooltipWidth - 7
      arrowTop = Math.max(20, Math.min(targetCenterY - top - 7, tooltipHeight - 20))
    }

    return { top, left, arrowTop, arrowLeft, arrowRotation }
  }

  let preferBelow = targetTop > tooltipHeight + margin
  if (!preferBelow && targetBottom + tooltipHeight + margin < viewportHeight) {
    preferBelow = true
  }

  let top: number
  let arrowTop: number
  let arrowLeft: number
  let arrowRotation: number

  if (preferBelow) {
    top = targetBottom + gap
    arrowTop = -7
    arrowRotation = 45
  } else {
    top = targetTop - tooltipHeight - gap
    arrowTop = tooltipHeight - 7
    arrowRotation = 225
  }

  let left = targetCenterX - tooltipWidth / 2
  left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin))
  arrowLeft = Math.max(20, Math.min(targetCenterX - left - 7, tooltipWidth - 20))

  top = Math.max(margin, Math.min(top, viewportHeight - tooltipHeight - margin))

  return { top, left, arrowTop, arrowLeft, arrowRotation }
}

export function TourOverlay() {
  const { isTourActive, currentStep, currentStepIndex, totalSteps, nextStep, prevStep, skipTour, finishTour } = useTour()
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ top: 200, left: 200, arrowTop: -7, arrowLeft: 30, arrowRotation: 45 })
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [showWelcome, setShowWelcome] = useState(false)
  const [showCompletion, setShowCompletion] = useState(false)

  useEffect(() => {
    if (!isTourActive || !currentStep) {
      setTargetRect(null)
      return
    }

    if (currentStep.id === 'welcome') {
      setShowWelcome(true)
      return
    }
    setShowWelcome(false)

    const timeoutId = setTimeout(() => {
      const rect = findTargetRect(currentStep)
      if (!rect && currentStep.target.startsWith('report-tab:')) {
        // Retry: the Reports component may not have rendered the tab buttons yet
        let retries = 0
        const retryId = setInterval(() => {
          const retryRect = findTargetRect(currentStep)
          if (retryRect) {
            setTargetRect(retryRect)
            clearInterval(retryId)
          } else {
            retries++
            if (retries >= 8) {
              // Fallback: show centered tooltip like content:general
              setTargetRect(null)
              clearInterval(retryId)
            }
          }
        }, 200)
      } else {
        setTargetRect(rect)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [isTourActive, currentStep])

  useEffect(() => {
    if (!isTourActive || !currentStep) return

    if (currentStep.target.startsWith('report-tab:')) {
      const reportId = currentStep.target.replace('report-tab:', '')
      let attempts = 0
      const tryClick = () => {
        const btn = document.querySelector(`[data-tour-report="${reportId}"]`) as HTMLElement | null
        if (btn) {
          btn.click()
        } else {
          attempts++
          if (attempts < 10) {
            setTimeout(tryClick, 200)
          }
        }
      }
      const timer = setTimeout(tryClick, 100)
      return () => clearTimeout(timer)
    }
  }, [isTourActive, currentStep])

  useEffect(() => {
    if (!isTourActive || !currentStep || currentStep.id === 'welcome') return
    if (targetRect === null && currentStep.target !== 'content:general' && !currentStep.target.startsWith('report-tab:')) return

    const isSidebarTarget = currentStep.target.startsWith('sidebar:')
    const isReportTabTarget = currentStep.target.startsWith('report-tab:')
    requestAnimationFrame(() => {
      const pos = computeTooltipPosition(targetRect, tooltipRef, isSidebarTarget, isReportTabTarget)
      setTooltipPos(pos)
    })
  }, [targetRect, isTourActive, currentStep])

  useEffect(() => {
    if (!isTourActive) return
    const handleResize = () => {
      if (currentStep && currentStep.id !== 'welcome') {
        const rect = findTargetRect(currentStep)
        setTargetRect(rect)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isTourActive, currentStep])

  const handleWelcomeStart = useCallback(() => {
    setShowWelcome(false)
    nextStep()
  }, [nextStep])

  const handleWelcomeSkip = useCallback(() => {
    setShowWelcome(false)
    skipTour()
  }, [skipTour])

  const handleNext = useCallback(() => {
    const isLast = currentStepIndex + 1 >= totalSteps
    if (isLast) {
      setShowCompletion(true)
      finishTour()
    } else {
      nextStep()
    }
  }, [currentStepIndex, totalSteps, nextStep, finishTour])

  const handleCompletionClose = useCallback(() => {
    setShowCompletion(false)
  }, [])

  if (!isTourActive || !currentStep) return null

  if (showWelcome) {
    return (
      <>
        <div className={styles.overlay} style={{ pointerEvents: 'auto' }}>
          <div className={styles.fullOverlay} />
        </div>
        <div className={styles.welcomeCard}>
          <div className={styles.welcomeIcon}>
            <svg viewBox="0 0 36 36" fill="none" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </div>
          <h2 className={styles.welcomeTitle}>Welcome to AllocatIQ</h2>
          <p className={styles.welcomeText}>
            Take a quick guided tour to learn how to set up your workspace and get the most out of the application.
            The tour takes about 3 minutes and covers everything you need to get started.
          </p>
          <ul className={styles.welcomeSteps}>
            <li className={styles.welcomeStepItem}><span className={styles.welcomeStepDot} />Understand master data and the setup sequence</li>
            <li className={styles.welcomeStepItem}><span className={styles.welcomeStepDot} />Set up role types, resources, customers, and partners</li>
            <li className={styles.welcomeStepItem}><span className={styles.welcomeStepDot} />Learn how allocations link resources to projects</li>
            <li className={styles.welcomeStepItem}><span className={styles.welcomeStepDot} />Configure partner rate sheet pricing</li>
            <li className={styles.welcomeStepItem}><span className={styles.welcomeStepDot} />Track time with timesheets and approvals</li>
          </ul>
          <div className={styles.welcomeControls}>
            <button className={styles.welcomeBtnSecondary} onClick={handleWelcomeSkip}>Skip for now</button>
            <button className={styles.welcomeBtnPrimary} onClick={handleWelcomeStart}>Start Tour</button>
          </div>
        </div>
      </>
    )
  }

  if (showCompletion) {
    return (
      <>
        <div className={styles.overlay} style={{ pointerEvents: 'auto' }}>
          <div className={styles.fullOverlay} />
        </div>
        <div className={styles.completionCard}>
          <div className={styles.completionIcon}>
            <svg viewBox="0 0 24 24" fill="none" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12L10 17L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className={styles.completionTitle}>Tour Complete!</h2>
          <p className={styles.completionText}>
            You now know the basics of AllocatIQ. Head to the Dashboard to see your organization overview,
            or start setting up your departments, resources, and projects.
          </p>
          <p className={styles.completionText} style={{ marginBottom: 24 }}>
            You can take the tour again anytime from the user menu in the top right corner.
          </p>
          <button className={styles.completionBtn} onClick={handleCompletionClose}>Get Started</button>
        </div>
      </>
    )
  }

  const spotlightStyle: React.CSSProperties = targetRect
    ? {
        top: targetRect.top - PADDING,
        left: targetRect.left - PADDING,
        width: targetRect.width + PADDING * 2,
        height: targetRect.height + PADDING * 2,
      }
    : {}

  const progressPct = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0
  const isFirst = currentStepIndex === 0
  const isLast = currentStepIndex + 1 >= totalSteps

  return (
    <>
      <div className={styles.overlay}>
        {targetRect ? (
          <div className={styles.spotlight} style={spotlightStyle} />
        ) : (
          <div className={styles.fullOverlay} />
        )}
      </div>
      <div
        ref={tooltipRef}
        className={styles.tooltip}
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        {targetRect && (
          <div
            className={styles.tooltipArrow}
            style={{
              top: tooltipPos.arrowTop,
              left: tooltipPos.arrowLeft,
              transform: `rotate(${tooltipPos.arrowRotation}deg)`,
            }}
          />
        )}
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
        </div>
        <div className={styles.stepBadge}>
          <span className={styles.stepBadgeDot} />
          Step {currentStepIndex + 1} of {totalSteps}
        </div>
        <h3 className={styles.title}>{currentStep.title}</h3>
        <p className={styles.description}>{currentStep.description}</p>
        {currentStep.listItems && currentStep.listItems.length > 0 && (
          <ol className={styles.stepList}>
            {currentStep.listItems.map((item, i) => (
              <li key={i} className={styles.stepListItem}>{item}</li>
            ))}
          </ol>
        )}
        {currentStep.cta && <div className={styles.cta}>{currentStep.cta}</div>}
        <div className={styles.controls}>
          <button className={styles.btnSkip} onClick={skipTour}>Skip Tour</button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className={styles.stepCounter}>{currentStepIndex + 1} / {totalSteps}</span>
            <button className={styles.btnNav + ' ' + styles.btnPrev} onClick={prevStep} disabled={isFirst}>
              Back
            </button>
            <button className={styles.btnNav + ' ' + styles.btnNext} onClick={handleNext}>
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
